import React, { useState, useEffect, useContext } from "react";
import { View, TouchableOpacity } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import commonStyles from "../../styles/commonStyles";
import AntDesign from "@expo/vector-icons/AntDesign";
import { getUserIdToUse } from "../../utils/getUserIdToUse";
import { ApiService } from "../../service/ApiService";
import { IUserTypes } from "../../types/IUserTypes";
import SkeletonLoader from "../../components/SkeletonLoader";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useIsFocused } from "@react-navigation/native";
import { RefreshContext } from "../../contexts/refreshContext";
import OverflowTooltipText from "../../components/OverflowTooltipText";
import AppText from "../../components/AppText";

type ProfileProps = {
  navigation: {
    navigate: (screen: string, params?: object) => void;
    goBack: () => void;
  };
};


const Profile: React.FC<ProfileProps> = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<IUserTypes | null>(null);
  const { refreshTrigger } = useContext(RefreshContext);
  const isFocused = useIsFocused();

  useEffect(() => {
    if (isFocused && refreshTrigger > 0) {
      fetchUserData();
    }
  }, [refreshTrigger]);

   useEffect(() => {
    const fetchUserFromStorage = async () => {
      try {
        const apiResponse = await AsyncStorage.getItem("apiResponse");
        const user = JSON.parse(apiResponse || "{}");
        setUserData(user);
      } catch (e) {
        setUserData(null);
      }
    };
    fetchUserFromStorage();
  }, []);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const userIdToUse = await getUserIdToUse();
      const response = await ApiService.get<any>(`portal/partner/${userIdToUse}`);
      console.log("the response ...... ", response)
      await AsyncStorage.setItem("apiResponse", JSON.stringify(response.data));
      setUserData(response.data); 
      setLoading(false);
    } catch (error) {
      setError("Error fetching data");
      setLoading(false);
    }
  };
  return (
    <SafeAreaView
      edges={["bottom", "left", "right"]}
      style={[commonStyles.container, commonStyles.screenBottomGap]}
    >
      <View style={commonStyles.headerContainer}>
        <AppText testID = "profile" style={commonStyles.header}>Profile</AppText>
      </View>
      {loading ? (
        <View testID="skeleton-loader" style={{ height: 50 }}>
          <SkeletonLoader rows={4} rowHeight={50} />
        </View>
      ) : (
        <>
          <View
            style={[
              commonStyles.subContainer,
              commonStyles.p14,
              commonStyles.mlar2,
            ]}
          >
            <TouchableOpacity
              onPress={() => navigation.navigate("PersonalDetails")}
            >
            <View style={[commonStyles.cardItem, { flexDirection: "column" }]}>
                <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                  <AppText testID="personal-details" style={commonStyles.detailsSubHeader}>
                    Personal Details
                  </AppText>
                  <AntDesign
                    testID="personal-details-arrow"
                    name="arrow-right"
                    size={22}
                    color="#666"
                    style={{ marginLeft: 8 }}
                  />
                </View>
                {(userData?.firstName || userData?.middleName || userData?.lastName) ? (
                  <AppText style={commonStyles.subtitle}>
                    {[userData?.firstName, userData?.middleName, userData?.lastName]
                      .filter(Boolean)
                      .join(" ")}
                  </AppText>
                ) : null}
                {userData?.email ? (
                  <AppText style={commonStyles.subtitle}>
                    {userData.email}
                  </AppText>
                ) : null}
              </View>
            </TouchableOpacity>
          </View>
          <View
            style={[
              commonStyles.subContainer,
              commonStyles.p14,
              commonStyles.mt20,
              commonStyles.mlar2,
            ]}
          >
            <TouchableOpacity onPress={() => navigation.navigate("Address")}>
             <View style={[commonStyles.cardItem, { flexDirection: "column" }]}>
                <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                  <AppText testID="address" style={commonStyles.detailsSubHeader}>
                    Address
                  </AppText>
                  <AntDesign
                    testID="address-arrow"
                    name="arrow-right"
                    size={22}
                    color="#666"
                    style={{ marginLeft: 8 }}
                  />
                </View>
                {(userData?.address?.street1 || userData?.address?.city) ? (
                  <AppText style={commonStyles.subtitle}>
                    {userData?.address?.street1 || ""}
                    {userData?.address?.street1 && userData?.address?.city ? ", " : ""}
                    {userData?.address?.city || ""}
                  </AppText>
                ) : null}
                {(userData?.address?.state || userData?.address?.zipCode) ? (
                  <AppText style={commonStyles.subtitle}>
                    {userData?.address?.state || ""}
                    {userData?.address?.state && userData?.address?.zipCode ? ", " : ""}
                    {userData?.address?.zipCode || ""}
                  </AppText>
                ) : null}
              </View>
            </TouchableOpacity>
          </View>
          <View
            style={[
              commonStyles.subContainer,
              commonStyles.p14,
              commonStyles.mt20,
              commonStyles.mlar2,
            ]}
          >
            <TouchableOpacity
              onPress={() => navigation.navigate("SpouseLinking")}
            >
            <View style={[commonStyles.cardItem, { flexDirection: "column" }]}>
              <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <AppText testID="spouse-details" style={commonStyles.detailsSubHeader}>
                    Spouse Details
                  </AppText>
                  {(userData?.partnerSpouse?.firstName || userData?.partnerSpouse?.lastName || userData?.partnerSpouse?.email) && (
                    <AntDesign
                      testID="spouse-checkmark"
                      name="check-circle"
                      size={16}
                      color="#4CAF50"
                      style={{ marginLeft: 8, marginTop: -7 }}
                    />
                  )}
                </View>
                <AntDesign
                  testID="spouse-arrow"
                  name="arrow-right"
                  size={22}
                  color="#666"
                  style={{ marginLeft: 8 }}
                />
              </View>
              {(userData?.partnerSpouse?.firstName || userData?.partnerSpouse?.lastName) ? (
                <AppText style={commonStyles.subtitle}>
                  {userData.partnerSpouse.firstName} {userData.partnerSpouse.lastName}
                </AppText>
              ) : null}
              {userData?.partnerSpouse?.email ? (
                <AppText style={commonStyles.subtitle}>
                  {userData.partnerSpouse.email}
                </AppText>
              ) : null}
              {(userData?.partnerSpouse?.firstName || userData?.partnerSpouse?.lastName || userData?.partnerSpouse?.email) && (
                <AppText style={[commonStyles.subText]}>
                  Spouse can view all details
                </AppText>
              )}
            </View>
            </TouchableOpacity>
          </View>
          {/* <View
            style={[
              commonStyles.subContainer,
              commonStyles.p14,
              commonStyles.mt20,
              commonStyles.mlar2,
            ]}
          >
            <TouchableOpacity
              onPress={() => navigation.navigate("BankDetails")}
            >
              <View style={commonStyles.cardItemblock}>
                <AppText style={commonStyles.detailsSubHeader}>Bank Details</AppText>
                <AntDesign
                  name="arrow-right"
                  size={22}
                  color="#666"
                  marginBottom={4}
                />
              </View>
            </TouchableOpacity>
          </View> */}
          {/* <View
            style={[
              commonStyles.subContainer,
              commonStyles.p14,
              commonStyles.mt20,
              commonStyles.mlar2,
            ]}
          >
            <TouchableOpacity onPress={() => navigation.navigate("IraAccount")}>
              <View style={commonStyles.cardItemblock}>
                <AppText style={commonStyles.detailsSubHeader}>IRA Account</AppText>
                <AntDesign
                  name="arrow-right"
                  size={22}
                  color="#666"
                  marginBottom={4}
                />
              </View>
            </TouchableOpacity>
          </View> */}
        </>
      )}
    </SafeAreaView>
  );
};

export default Profile;
